import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useSpaces } from "../context/SpaceProvider";
import useRetryFetch from "./useRetryFetch";
import {
  CompanionMessage,
  listCompanionMessages,
} from "../api/companionMessages";

export interface TaggedCompanionMessage extends CompanionMessage {
  spaceName: string;
  spaceColor: string;
}

interface UseAllSpacesCompanionMessagesReturn {
  messages: TaggedCompanionMessage[];
  isLoading: boolean;
}

const MESSAGES_PER_SPACE = 3;

/**
 * Aggregates the latest companion messages from ALL identity spaces.
 * Each message is tagged with its space name and color.
 */
export function useAllSpacesCompanionMessages(): UseAllSpacesCompanionMessagesReturn {
  const { spaces } = useSpaces();
  const retryFetch = useRetryFetch();

  const activeSpaces = useMemo(
    () => spaces.filter((s) => s.status === "active" || s.status === "created"),
    [spaces]
  );

  const queries = useQueries({
    queries: activeSpaces.map((space) => ({
      queryKey: ["companion-messages-home", space._id],
      queryFn: () =>
        listCompanionMessages(retryFetch, space._id, MESSAGES_PER_SPACE, 0),
      staleTime: 5 * 60 * 1000,
      enabled: !!space._id,
    })),
  });

  const messages = useMemo(() => {
    const tagged: TaggedCompanionMessage[] = [];
    for (let i = 0; i < queries.length; i++) {
      const result = queries[i];
      const space = activeSpaces[i];
      if (result.data?.messages) {
        for (const msg of result.data.messages) {
          tagged.push({
            ...msg,
            spaceName: space.name,
            spaceColor: space.color || "#6366f1",
          });
        }
      }
    }
    // Sort by date descending
    tagged.sort(
      (a, b) =>
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
    );
    return tagged;
  }, [queries, activeSpaces]);

  const isLoading = queries.some((q) => q.isLoading);

  return { messages, isLoading };
}
